import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  Divider,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  ADMIN_HOME_FAQS_QUERY,
  ADMIN_UPSERT_HOME_FAQ_MUTATION,
  ADMIN_DELETE_HOME_FAQ_MUTATION,
} from "../../graphql/faqs";

type FaqRow = {
  id: string;
  question: string;
  answer: string;
  position: number;
  active: boolean;
};

type FaqForm = {
  id?: string;
  question: string;
  answer: string;
  position: number;
  active: boolean;
};

const emptyForm: FaqForm = {
  question: "",
  answer: "",
  position: 0,
  active: true,
};

export default function HomeFaqsAdminPage() {
  const { data, loading, error, refetch } = useQuery(ADMIN_HOME_FAQS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [upsert, upsertState] = useMutation(ADMIN_UPSERT_HOME_FAQ_MUTATION);
  const [deleteFaq, deleteState] = useMutation(ADMIN_DELETE_HOME_FAQ_MUTATION);

  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FaqRow | null>(null);
  const [form, setForm] = useState<FaqForm>(emptyForm);

  const faqs: FaqRow[] = data?.adminHomeFaqs ?? [];

  const openCreate = () => {
    setForm(emptyForm);
    setOpenForm(true);
  };
  const openEdit = (r: FaqRow) => {
    setForm({
      id: r.id,
      question: r.question,
      answer: r.answer,
      position: r.position,
      active: r.active,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    const res = await upsert({
      variables: {
        id: form.id || null,
        question: form.question.trim(),
        answer: form.answer.trim(),
        position: form.position,
        active: form.active,
      },
    });
    const errors = res?.data?.adminUpsertHomeFaq?.errors ?? [];
    if (errors.length) return;
    setOpenForm(false);
    await refetch();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteFaq({ variables: { id: confirmDelete.id } });
    setConfirmDelete(null);
    await refetch();
  };

  const formErrors: string[] =
    upsertState.data?.adminUpsertHomeFaq?.errors ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Homepage FAQs</Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            These FAQs appear on the homepage after the About Us section. Max 3
            FAQs recommended for best display.
          </Alert>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add FAQ
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>Order</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Question</TableCell>
                <TableCell>Answer</TableCell>
                <TableCell sx={{ width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No FAQs added yet.
                  </TableCell>
                </TableRow>
              )}
              {faqs.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.position}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {r.question}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        maxWidth: 400,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontSize: 13,
                        color: "text.secondary",
                      }}
                    >
                      {r.answer}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.active ? "Active" : "Hidden"}
                      color={r.active ? "success" : "default"}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FORM DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {form.id ? "Edit FAQ" : "Add Homepage FAQ"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.join(" • ")}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Question"
              value={form.question}
              onChange={(e) =>
                setForm((p) => ({ ...p, question: e.target.value }))
              }
              fullWidth
              placeholder="e.g. What types of gold do you offer?"
            />
            <TextField
              label="Answer"
              value={form.answer}
              onChange={(e) =>
                setForm((p) => ({ ...p, answer: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
              placeholder="Write a detailed answer..."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Display Order"
                type="number"
                value={form.position}
                onChange={(e) =>
                  setForm((p) => ({ ...p, position: Number(e.target.value) }))
                }
                fullWidth
                helperText="Lower = shown first"
              />
              <TextField
                select
                label="Status"
                value={form.active ? "active" : "hidden"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    active: e.target.value === "active",
                  }))
                }
                fullWidth
              >
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </TextField>
            </Stack>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setOpenForm(false)}
              disabled={upsertState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                upsertState.loading ||
                !form.question.trim() ||
                !form.answer.trim()
              }
            >
              {upsertState.loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Delete FAQ?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete: <b>"{confirmDelete?.question}"</b>
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setConfirmDelete(null)}
              disabled={deleteState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteState.loading}
            >
              {deleteState.loading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
